import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

// DMC Indonesia → India invoice, generated as a Word file from whatever is typed here.
// Nothing is saved: values (number, dates, table, bank details...) are free to differ
// from the booking. Seller/buyer/bank are pre-filled from master config.

interface LineItem {
  description: string;
  qty: number;
  rate: number;
}

const today = () => new Date().toISOString().split('T')[0];
const addDays = (iso: string, days: number) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const textareaClass = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const DmcInvoiceGenerator: React.FC = () => {
  const [generating, setGenerating] = useState(false);

  const [sellerName, setSellerName] = useState('');
  const [sellerAddress, setSellerAddress] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerAddress, setBuyerAddress] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [missingKeys, setMissingKeys] = useState<string[]>([]);
  const [bankDetails, setBankDetails] = useState('');
  const [logoInfo, setLogoInfo] = useState<{ key: string; configured: boolean } | null>(null);

  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate] = useState(addDays(today(), 7));
  const [currency, setCurrency] = useState('USD');
  const [guestName, setGuestName] = useState('');
  const [description, setDescription] = useState('Tour package services');
  const [travelDate, setTravelDate] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([{ description: 'Bali Tour Package', qty: 1, rate: 0 }]);
  const [notes, setNotes] = useState('');

  const getToken = () => sessionStorage.getItem('jwt_token');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/india-ops/dmc-invoice/defaults', { headers: { 'Authorization': `Bearer ${getToken()}` } });
        const data = await res.json();
        if (data.success) {
          setSellerName(data.data.seller_name || '');
          setSellerAddress(data.data.seller_address || '');
          setBuyerName(data.data.buyer_name || '');
          setBuyerAddress(data.data.buyer_address || '');
          setBuyerEmail(data.data.buyer_email || '');
          setBankDetails(data.data.bank_details || '');
          setMissingKeys(data.data.missing_keys || []);
          setLogoInfo({ key: data.data.logo_key, configured: !!data.data.logo_configured });
        }
      } catch (e) {
        console.error('Error loading DMC invoice defaults:', e);
      }
    })();
  }, []);

  const updateItem = (i: number, field: keyof LineItem, value: any) => {
    setLineItems(items => items.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  };

  const total = lineItems.reduce((s, i) => s + (i.qty || 0) * (i.rate || 0), 0);

  const generate = async () => {
    if (!invoiceNumber.trim()) { alert('Enter an invoice number'); return; }
    if (!sellerName.trim() || !buyerName.trim()) { alert('Enter the From and To company names'); return; }
    if (!lineItems.some(i => i.description.trim() && i.qty && i.rate)) { alert('Add at least one line item with description, qty and rate'); return; }
    setGenerating(true);
    try {
      const res = await fetch('/api/india-ops/dmc-invoice/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}` },
        body: JSON.stringify({
          invoice_number: invoiceNumber, invoice_date: invoiceDate, due_date: dueDate || null, currency,
          guest_name: guestName, description, travel_date: travelDate, line_items: lineItems, notes,
          seller_name: sellerName, seller_address: sellerAddress, buyer_name: buyerName, buyer_address: buyerAddress,
          buyer_email: buyerEmail, bank_details: bankDetails,
        }),
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch { /* non-JSON error */ }
        alert(`Error generating invoice${detail ? `: ${detail}` : ''}`);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DMC_Invoice_${invoiceNumber.replace(/[\/\s]/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Error generating DMC invoice:', e);
      alert('Error generating invoice');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
      <div className="mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">🧾 DMC Invoice Generation</h2>
          <p className="text-xs text-gray-500">Indonesian DMC → Lexora India. Generates a Word file only — nothing is saved.</p>
          {logoInfo && (
            <p className={`text-xs mt-1 ${logoInfo.configured ? 'text-green-700' : 'text-amber-700'}`}>
              {logoInfo.configured
                ? `Logo: "${logoInfo.key}" from Master Configurations`
                : `Logo: "${logoInfo.key}" not set — using the IIW logo. Add it in Settings → Master Configurations (type: File Upload).`}
            </p>
          )}
          {missingKeys.length > 0 && (
            <p className="text-xs mt-1 text-amber-700">
              Not set in Settings → Master Configurations (fill in below, or add them there): {missingKeys.join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* From / To */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">From (Indonesian DMC)</h4>
          <Input value={sellerName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellerName(e.target.value)} placeholder="Company name" className="text-sm mb-2" />
          <textarea value={sellerAddress} onChange={e => setSellerAddress(e.target.value)} placeholder="Address" rows={3} className={textareaClass} />
        </div>
        <div className="p-3 bg-gray-50 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">To (Lexora India)</h4>
          <Input value={buyerName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuyerName(e.target.value)} placeholder="Company name" className="text-sm mb-2" />
          <Input value={buyerEmail} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBuyerEmail(e.target.value)} placeholder="Email" className="text-sm mb-2" />
          <textarea value={buyerAddress} onChange={e => setBuyerAddress(e.target.value)} placeholder="Address" rows={3} className={textareaClass} />
        </div>
      </div>

      {/* Header fields */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number *</label>
          <Input value={invoiceNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNumber(e.target.value)} placeholder="e.g. IIW/INV/2026/0101" className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date *</label>
          <Input type="date" value={invoiceDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceDate(e.target.value)} className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
          <Input type="date" value={dueDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDueDate(e.target.value)} className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
          <Select value={currency} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCurrency(e.target.value)} className="text-sm">
            <option value="USD">USD</option>
            <option value="IDR">IDR</option>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Guest Name</label>
          <Input value={guestName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuestName(e.target.value)} placeholder="Lead guest" className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Subject / Description</label>
          <Input value={description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)} className="text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Travel Date (optional)</label>
          <Input type="date" value={travelDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTravelDate(e.target.value)} className="text-sm" />
        </div>
      </div>

      {/* Line items */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Invoice Items ({currency})</label>
          <button onClick={() => setLineItems([...lineItems, { description: '', qty: 1, rate: 0 }])} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Add Item</button>
        </div>
        <div className="space-y-2">
          {lineItems.map((item, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <Input value={item.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'description', e.target.value)} placeholder="Description" className="col-span-12 sm:col-span-6 text-sm" />
              <Input type="number" value={item.qty} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'qty', parseFloat(e.target.value) || 0)} placeholder="Qty" className="col-span-3 sm:col-span-1 text-sm" />
              <Input type="number" value={item.rate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateItem(i, 'rate', parseFloat(e.target.value) || 0)} placeholder="Rate" className="col-span-4 sm:col-span-2 text-sm" />
              <div className="col-span-4 sm:col-span-2 p-2 bg-gray-100 rounded text-sm text-right">{((item.qty || 0) * (item.rate || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <button onClick={() => setLineItems(lineItems.filter((_, idx) => idx !== i))} disabled={lineItems.length === 1} className="col-span-1 text-red-400 hover:text-red-600 disabled:opacity-30">✕</button>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3">
          <div className="text-sm font-semibold">Amount: {currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      {/* Bank details + notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Bank Details for Remittance</label>
          <textarea value={bankDetails} onChange={e => setBankDetails(e.target.value)} rows={5} className={textareaClass} placeholder="Bank name, account name, account number, SWIFT..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={5} className={textareaClass} />
        </div>
      </div>

      <Button onClick={generate} disabled={generating} className="text-sm">
        {generating ? 'Generating...' : '🖨️ Generate Invoice (Word)'}
      </Button>
    </div>
  );
};

export default DmcInvoiceGenerator;
